// Utilities

const handleize = function (str) {
  return str.toLowerCase()
    .replace(/'/g, "")
    .replace(/[^\w\u00C0-\u024f]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

const getHeadingUrl = function (heading) {
  return `${window.location.origin}${window.location.pathname}${window.location.search}#${heading.id}`;
};

const clearVisibleAnchor = function (exceptHeading) {
  document.querySelectorAll(".body-col :is(h2, h3).is-anchor-visible").forEach((heading) => {
    if (heading !== exceptHeading) {
      heading.classList.remove("is-anchor-visible");
    }
  });
};

const copyHeadingUrl = async function (anchor, heading) {
  const headingUrl = anchor.dataset.copyUrl || getHeadingUrl(heading);
  let copied = false;

  try {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(headingUrl);
      copied = true;
    }
  } catch {
    copied = false;
  }

  if (!copied) {
    window.location.hash = heading.id;
  }

  anchor.classList.add("is-copied");
  anchor.setAttribute("aria-label", copied ? "Copied link to this section" : "Link opened for this section");

  window.setTimeout(() => {
    anchor.classList.remove("is-copied");
    anchor.setAttribute("aria-label", "Copy link to this section");
  }, 1500);
};

const addHeadingAnchor = function (heading) {
  let anchor = heading.querySelector(":scope > .heading-anchor");

  if (!anchor) {
    anchor = document.createElement("a");
    anchor.className = "heading-anchor";
    anchor.textContent = "#";
    anchor.setAttribute("aria-label", "Copy link to this section");
    heading.append(anchor);
  }

  anchor.href = `#${heading.id}`;
  anchor.dataset.copyUrl = getHeadingUrl(heading);

  if (!anchor.dataset.anchorCopyBound) {
    anchor.dataset.anchorCopyBound = "true";

    anchor.addEventListener("click", async (event) => {
      event.preventDefault();
      event.stopPropagation();

      clearVisibleAnchor(heading);
      heading.classList.add("is-anchor-visible");

      await copyHeadingUrl(anchor, heading);
    });
  }
};

const getHeadingLabel = function (heading) {
  return Array.from(heading.childNodes)
    .filter((node) => node.nodeType === Node.TEXT_NODE || !node.classList?.contains("heading-anchor"))
    .map((node) => node.textContent.trim())
    .filter(Boolean)
    .join(" ");
};

const setTocOpen = function (toc, isOpen) {
  const toggle = toc.querySelector(".toc-toggle");
  const tocList = toc.querySelector("ol");
  const isMobileToc = window.matchMedia("(max-width: 860px)").matches;

  toc.classList.toggle("is-open", isOpen);
  toggle?.setAttribute("aria-expanded", isOpen ? "true" : "false");

  if (tocList) {
    tocList.hidden = isMobileToc && !isOpen;
  }
};

const setActiveTocLink = function (toc, activeHeading) {
  const links = Array.from(toc.querySelectorAll('ol a[href*="#"]'));
  const activeLink = links.find((link) => new URL(link.href).hash === `#${activeHeading.id}`);
  const currentLabel = toc.querySelector(".toc-current");

  links.forEach((link) => {
    const isActive = link === activeLink;
    link.classList.toggle("is-active", isActive);
    link.setAttribute("aria-current", isActive ? "true" : "false");
  });

  if (currentLabel && activeLink) {
    currentLabel.textContent = activeLink.textContent;
  }
};

const enhanceToc = function (toc, headings) {
  const label = toc.querySelector(".label");
  const tocList = toc.querySelector("ol");
  const mobileTocMedia = window.matchMedia("(max-width: 860px)");

  if (!label || !tocList || toc.dataset.isEnhanced) {
    return;
  }

  toc.dataset.isEnhanced = "true";
  tocList.id = tocList.id || "sanity-article-toc-list";

  const toggle = document.createElement("button");
  toggle.className = "toc-toggle";
  toggle.type = "button";
  toggle.setAttribute("aria-expanded", "false");
  toggle.setAttribute("aria-controls", tocList.id);

  const toggleLabel = document.createElement("span");
  toggleLabel.className = "toc-toggle-label";
  toggleLabel.textContent = label.textContent;

  const currentLabel = document.createElement("span");
  currentLabel.className = "toc-current";
  currentLabel.textContent = headings[0] ? getHeadingLabel(headings[0]) : "";

  const toggleIcon = document.createElement("span");
  toggleIcon.className = "toc-toggle-icon";
  toggleIcon.setAttribute("aria-hidden", "true");

  toggle.append(toggleLabel, currentLabel, toggleIcon);
  label.after(toggle);

  setTocOpen(toc, false);

  toggle.addEventListener("click", () => {
    setTocOpen(toc, !toc.classList.contains("is-open"));
  });

  const syncTocListVisibility = () => {
    setTocOpen(toc, toc.classList.contains("is-open"));
  };

  if (mobileTocMedia.addEventListener) {
    mobileTocMedia.addEventListener("change", syncTocListVisibility);
  } else {
    mobileTocMedia.addListener(syncTocListVisibility);
  }

  tocList.addEventListener("click", (event) => {
    const link = event.target.closest("a");

    if (!link) {
      return;
    }

    const linkedHeading = document.querySelector(new URL(link.href).hash);
    if (linkedHeading) {
      setActiveTocLink(toc, linkedHeading);
    }

    setTocOpen(toc, false);
  });

  document.addEventListener("click", (event) => {
    if (toc.contains(event.target)) {
      return;
    }

    setTocOpen(toc, false);
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape" || !toc.classList.contains("is-open")) {
      return;
    }

    setTocOpen(toc, false);
    toggle.focus();
  });

  const visibleHeadings = new Set();
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        visibleHeadings.add(entry.target);
      } else {
        visibleHeadings.delete(entry.target);
      }
    });

    const visibleHeading = headings.find((heading) => visibleHeadings.has(heading));

    if (visibleHeading) {
      setActiveTocLink(toc, visibleHeading);
    }
  }, { rootMargin: "0px 0px -70% 0px" });

  headings.forEach((heading) => observer.observe(heading));

  if (headings[0]) {
    setActiveTocLink(toc, headings[0]);
  }
};

// Main

const post = document.querySelector("article.post");
const isCoarsePointer = window.matchMedia ? window.matchMedia("(hover: none), (pointer: coarse)") : { matches: false };

// Build a sequential list of h2/h3 tags by traversing the DOM with a stack

const headings = [];
const stack = post ? [post] : [];
while (stack.length > 0) {
  const node = stack.pop();
  if ((node.tagName === "H2" || node.tagName === "H3") && Boolean(getHeadingLabel(node))) {
    headings.push(node);
  }
  const children = [...node.children];
  while (children.length > 0) {
    stack.push(children.pop());
  }
}

// Process content headings:
// - Assign numbering
// - Assign IDs to content headings
// Heading text may be repeated, especially in subtitles, so prepend it with sequential numbering

let headingNum = 0;
let subheadingNum = 0;
for (const heading of headings) {
  if (heading.tagName === "H2") {
    heading.dataset.tocIndex = `${++headingNum}`;
    subheadingNum = 0;
  } else {
    heading.dataset.tocIndex = `${headingNum}-${++subheadingNum}`;
  }
  if (!heading.id) {
    heading.id = `content-${heading.dataset.tocIndex}-${handleize(heading.innerText)}`;
  }

  // Add heading anchor links

  addHeadingAnchor(heading);

  if (!heading.dataset.anchorToggleBound) {
    heading.dataset.anchorToggleBound = "true";

    heading.addEventListener("click", (event) => {
      if (!isCoarsePointer.matches || event.target.closest(".heading-anchor")) {
        return;
      }

      clearVisibleAnchor(heading);
      heading.classList.add("is-anchor-visible");
    });
  }
}

// Bind clicks outside headings to clear anchor links

if (!document.documentElement.dataset.sanityHeadingAnchorsBound) {
  document.documentElement.dataset.sanityHeadingAnchorsBound = "true";

  document.addEventListener("click", (event) => {
    if (!isCoarsePointer.matches || event.target.closest(".body-col :is(h2, h3)")) {
      return;
    }

    clearVisibleAnchor();
  });
}

// Build ToC

const tocNav = post?.querySelector(".toc-nav");
if (tocNav && !tocNav.dataset.isPopulated) {
  tocNav.dataset.isPopulated = "true";
  const templateTocLi = tocNav.querySelector('[data-template="toc-li"]');
  const tocList = document.createElement("ol");
  if (templateTocLi) {
    for (const heading of headings) {
      // Standard template business:
      // - Get clone of template content DocumentFragment,
      // - Populate clone fragment with real values,
      // - Append clone fragment to DOM (which actually appends the Fragment's innerHtml)
      const headingContent = getHeadingLabel(heading);
      if (!headingContent) {
        continue;
      }

      const clone = templateTocLi.content.cloneNode(true);
      const cloneAnchor = clone.querySelector("a");
      cloneAnchor.classList.add(`from-${heading.tagName}`);
      cloneAnchor.href = getHeadingUrl(heading);
      cloneAnchor.textContent = headingContent;
      tocList.appendChild(clone);
    }
  }
  if (headings.length < 3) {
    tocNav.closest(".layout")?.classList.add("layout--toc-hidden");
  }
  tocNav.appendChild(tocList);
}

if (tocNav) {
  enhanceToc(tocNav, headings);
}
